import { useState, useEffect } from 'react';
import { getAllTeams, getAllEngagements, getAllSampleRuns,
         flagEngagement, unflagEngagement, suspendTeam, unsuspendTeam } from '../lib/supabase';
import VerifyPanel from '../components/VerifyPanel';

const NAVY = '#0D2461', AMBER = '#F5A800', BORDER = '#c8d0e0';

const STATUS_CONFIG = {
  draft:             { label: 'Draft',          color: '#8a96b0', bg: '#f0f2f8' },
  awaiting_approval: { label: 'Awaiting Approval', color: '#c98a00', bg: '#fff8e6' },
  sampled:           { label: 'Sampled',         color: '#1a5080', bg: '#e0f0f8' },
  awaiting_scoring:  { label: 'In Progress',     color: '#6040a0', bg: '#f0e8ff' },
  complete:          { label: 'Complete',         color: '#1a5c3a', bg: '#e4f2eb' },
};

const APPROVAL_CONFIG = {
  pending:           { label: 'Not Submitted',   color: '#8a96b0' },
  awaiting_approval: { label: 'Awaiting Approval', color: '#c98a00' },
  approved:          { label: 'Approved',         color: '#1a5c3a' },
  rejected:          { label: 'Rejected',         color: '#c03a1a' },
};

export default function AuditorPage({ session, profile, onBack }) {
  const [tab,         setTab]         = useState('projects');
  const [teams,       setTeams]       = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [sampleRuns,  setSampleRuns]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [flagModal,   setFlagModal]   = useState(null); // { eng, mode: 'flag'|'unflag' }
  const [suspendModal,setSuspendModal]= useState(null); // { team, mode: 'suspend'|'unsuspend' }
  const [noteInput,   setNoteInput]   = useState('');
  const [filter,      setFilter]      = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [teamsRes, engsRes, runsRes] = await Promise.all([
      getAllTeams(), getAllEngagements(), getAllSampleRuns(),
    ]);
    setTeams(teamsRes.data || []);
    setEngagements(engsRes.data || []);
    setSampleRuns(runsRes.data || []);
    setLoading(false);
  };

  const handleFlag = async () => {
    if (flagModal.mode === 'flag') {
      await flagEngagement(flagModal.eng.id, noteInput);
    } else {
      await unflagEngagement(flagModal.eng.id);
    }
    setFlagModal(null); setNoteInput('');
    loadAll();
  };

  const handleSuspend = async () => {
    if (suspendModal.mode === 'suspend') {
      await suspendTeam(suspendModal.team.id, noteInput);
    } else {
      await unsuspendTeam(suspendModal.team.id);
    }
    setSuspendModal(null); setNoteInput('');
    loadAll();
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }) : '—';
  const fmtDt = (d) => d ? new Date(d).toLocaleString() : '—';

  const filteredEngs = engagements.filter(e =>
    !filter || e.client_name?.toLowerCase().includes(filter.toLowerCase()) ||
    e.teams?.name?.toLowerCase().includes(filter.toLowerCase())
  );

  // Group engagements by client_name for abuse detection
  const clientGroups = {};
  engagements.forEach(e => {
    const key = e.client_name || 'Unknown';
    if (!clientGroups[key]) clientGroups[key] = [];
    clientGroups[key].push(e);
  });
  const multiDrawClients = Object.entries(clientGroups)
    .filter(([, engs]) => engs.reduce((s, e) => s + (e.sample_runs_count || 0), 0) > 1 ||
      engs.length > 1)
    .sort(([,a],[,b]) => b.length - a.length);

  return (
    <div style={page}>
      {/* Nav */}
      <div style={nav}>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          <button style={backBtn} onClick={onBack}>← Dashboard</button>
          <div style={{width:1,height:28,background:'rgba(255,255,255,0.15)'}}/>
          <div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontWeight:700,fontSize:15,color:'white'}}>
              Auditor Dashboard
            </div>
            <div style={{fontFamily:"'Roboto Mono',monospace",fontSize:10,color:'rgba(255,255,255,0.45)'}}>
              Platform-wide view · Read/write access
            </div>
          </div>
        </div>
        <div style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'rgba(255,255,255,0.5)'}}>
          {profile?.full_name || session.user.email}
        </div>
      </div>

      {/* Tabs */}
      <div style={tabBar}>
        {[['projects','All Projects'],['teams','All Teams'],['runs','Sample Run Log'],['flags','Flagged & Suspended'],['verify','Verify Sample']].map(([key,lbl]) => (
          <button key={key} style={{...tabBtn, ...(tab===key?tabBtnActive:{})}} onClick={() => setTab(key)}>
            {lbl}
            {key === 'flags' && (engagements.filter(e=>e.flagged).length + teams.filter(t=>t.suspended).length) > 0 &&
              <span style={alertDot}>{engagements.filter(e=>e.flagged).length + teams.filter(t=>t.suspended).length}</span>}
          </button>
        ))}
      </div>

      <div style={body}>
        {loading ? (
          <div style={{fontFamily:"'Roboto Mono',monospace",fontSize:13,color:'#8a96b0',textAlign:'center',padding:60}}>Loading…</div>
        ) : (
          <>
            {/* ── Projects tab ── */}
            {tab === 'projects' && (
              <>
                <div style={headerRow}>
                  <div>
                    <div style={eyebrow}>Platform Overview</div>
                    <h1 style={pageTitle}>All Projects ({engagements.length})</h1>
                  </div>
                  <input style={searchInput} placeholder="Filter by client or firm…"
                    value={filter} onChange={e => setFilter(e.target.value)} />
                </div>

                {multiDrawClients.length > 0 && (
                  <div style={{...alertBox, marginBottom:20}}>
                    ⚠ <strong>{multiDrawClients.length} client{multiDrawClients.length!==1?'s':''}</strong> have multiple projects or draws —
                    review for potential sampling workarounds:
                    {' '}{multiDrawClients.slice(0,5).map(([name]) => name).join(', ')}
                    {multiDrawClients.length > 5 && ` +${multiDrawClients.length-5} more`}
                  </div>
                )}

                <table style={table}>
                  <thead><tr>
                    {['Client','Firm','Tax Years','Status','Approval','Samples Drawn','Updated','Actions'].map(h=><th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {filteredEngs.map(eng => {
                      const st = STATUS_CONFIG[eng.status] || STATUS_CONFIG.draft;
                      const ap = APPROVAL_CONFIG[eng.approval_status] || APPROVAL_CONFIG.pending;
                      return (
                        <tr key={eng.id} style={{borderBottom:'1px solid #f0f2f8',background:eng.flagged?'#fff8e6':'white'}}>
                          <td style={{...td,fontWeight:700,color:NAVY}}>
                            {eng.flagged && <span style={{marginRight:6}}>🚩</span>}
                            {eng.client_name}
                            {eng.client_group && <div style={{fontSize:10,color:'#8a96b0',marginTop:2}}>{eng.client_group}</div>}
                          </td>
                          <td style={td}>{eng.teams?.name || '—'}</td>
                          <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{eng.tax_years || '—'}</td>
                          <td style={td}><span style={{...statusPill,color:st.color,background:st.bg}}>{st.label}</span></td>
                          <td style={td}><span style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:ap.color}}>{ap.label}</span></td>
                          <td style={{...td,fontFamily:"'Roboto Mono',monospace",textAlign:'center'}}>{eng.archived?<span style={{color:'#8a96b0'}}>archived</span>:'—'}</td>
                          <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11}}>{fmt(eng.updated_at)}</td>
                          <td style={td}>
                            {eng.flagged ? (
                              <button style={unflagBtn} onClick={() => { setFlagModal({eng, mode:'unflag'}); setNoteInput(''); }}>Unflag</button>
                            ) : (
                              <button style={flagBtn} onClick={() => { setFlagModal({eng, mode:'flag'}); setNoteInput(''); }}>🚩 Flag</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {/* ── Teams tab ── */}
            {tab === 'teams' && (
              <>
                <div style={headerRow}>
                  <div>
                    <div style={eyebrow}>Platform Overview</div>
                    <h1 style={pageTitle}>All Teams ({teams.length})</h1>
                  </div>
                </div>
                <table style={table}>
                  <thead><tr>
                    {['Team Name','Team ID','Credits','Projects','Status','Actions'].map(h=><th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {teams.map(team => {
                      const teamEngs = engagements.filter(e => e.team_id === team.id);
                      return (
                        <tr key={team.id} style={{borderBottom:'1px solid #f0f2f8',background:team.suspended?'#fff0ee':'white'}}>
                          <td style={{...td,fontWeight:700,color:NAVY}}>{team.name}</td>
                          <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#8a96b0'}}>{team.id.slice(0,8)}…</td>
                          <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontWeight:700,color:team.credits<2?'#c03a1a':'#1a5c3a'}}>{team.credits}</td>
                          <td style={td}>{teamEngs.length}</td>
                          <td style={td}>
                            {team.suspended
                              ? <span style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#c03a1a',fontWeight:700}}>⛔ Suspended{team.suspension_note?` — ${team.suspension_note}`:''}</span>
                              : <span style={{fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#1a5c3a'}}>Active</span>}
                          </td>
                          <td style={td}>
                            {team.suspended ? (
                              <button style={unflagBtn} onClick={() => { setSuspendModal({team, mode:'unsuspend'}); setNoteInput(''); }}>Reinstate</button>
                            ) : (
                              <button style={{...flagBtn,color:'#c03a1a',borderColor:'#c03a1a'}}
                                onClick={() => { setSuspendModal({team, mode:'suspend'}); setNoteInput(''); }}>⛔ Suspend</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}

            {/* ── Sample run log tab ── */}
            {tab === 'runs' && (
              <>
                <div style={headerRow}>
                  <div>
                    <div style={eyebrow}>Platform Overview</div>
                    <h1 style={pageTitle}>Sample Run Log ({sampleRuns.length})</h1>
                  </div>
                </div>
                <table style={table}>
                  <thead><tr>
                    {['Run #','Client','Firm','Date/Time','Seed','Sample Size','Proj. RP','Settings'].map(h=><th key={h} style={th}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {sampleRuns.map(run => (
                      <tr key={run.id} style={{borderBottom:'1px solid #f0f2f8'}}>
                        <td style={{...td,fontWeight:700,color:NAVY}}>#{run.run_number}</td>
                        <td style={{...td,fontWeight:600,color:NAVY}}>{run.engagements?.client_name || '—'}</td>
                        <td style={td}>{run.engagements?.teams?.name || '—'}</td>
                        <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11}}>{fmtDt(run.drawn_at)}</td>
                        <td style={{...td,fontFamily:"'Roboto Mono',monospace"}}>{run.seed}</td>
                        <td style={td}>{run.total_sample}</td>
                        <td style={td}>{run.projected_rp != null ? (run.projected_rp*100).toFixed(1)+'%' : '—'}</td>
                        <td style={{...td,fontSize:10,color:'#8a96b0'}}>
                          {run.settings ? `${run.settings.numStrata||5} strata · n=${run.settings.fixedN||5}/str${run.settings.tossEnabled?' · toss on':''}` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {/* ── Verify tab ── */}
            {tab === 'verify' && (
              <VerifyPanel engagements={engagements} />
            )}

            {/* ── Flags tab ── */}
            {tab === 'flags' && (
              <>
                <div style={headerRow}>
                  <div>
                    <div style={eyebrow}>Auditor Actions</div>
                    <h1 style={pageTitle}>Flagged Projects &amp; Suspended Teams</h1>
                  </div>
                </div>
                {engagements.filter(e=>e.flagged).length === 0 && teams.filter(t=>t.suspended).length === 0 ? (
                  <div style={{fontFamily:"'Roboto Mono',monospace",fontSize:13,color:'#8a96b0',padding:'40px 0'}}>No flagged projects or suspended teams.</div>
                ) : (
                  <>
                    {engagements.filter(e=>e.flagged).length > 0 && (
                      <>
                        <div style={sectionLabel}>Flagged Projects ({engagements.filter(e=>e.flagged).length})</div>
                        <table style={{...table,marginBottom:24}}>
                          <thead><tr>{['Client','Firm','Flag Note','Flagged','Actions'].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {engagements.filter(e=>e.flagged).map(eng => (
                              <tr key={eng.id} style={{borderBottom:'1px solid #f0f2f8',background:'#fff8e6'}}>
                                <td style={{...td,fontWeight:700,color:NAVY}}>🚩 {eng.client_name}</td>
                                <td style={td}>{eng.teams?.name || '—'}</td>
                                <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#c98a00'}}>{eng.flag_note || '—'}</td>
                                <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11}}>{fmt(eng.updated_at)}</td>
                                <td style={td}><button style={unflagBtn} onClick={() => { setFlagModal({eng,mode:'unflag'}); setNoteInput(''); }}>Unflag</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {teams.filter(t=>t.suspended).length > 0 && (
                      <>
                        <div style={sectionLabel}>Suspended Teams ({teams.filter(t=>t.suspended).length})</div>
                        <table style={table}>
                          <thead><tr>{['Team','Suspension Note','Actions'].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
                          <tbody>
                            {teams.filter(t=>t.suspended).map(team => (
                              <tr key={team.id} style={{borderBottom:'1px solid #f0f2f8',background:'#fff0ee'}}>
                                <td style={{...td,fontWeight:700,color:'#c03a1a'}}>⛔ {team.name}</td>
                                <td style={{...td,fontFamily:"'Roboto Mono',monospace",fontSize:11,color:'#c03a1a'}}>{team.suspension_note || '—'}</td>
                                <td style={td}><button style={unflagBtn} onClick={() => { setSuspendModal({team,mode:'unsuspend'}); setNoteInput(''); }}>Reinstate</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* ── Flag modal ── */}
      {flagModal && (
        <div style={overlay}>
          <div style={modal}>
            <h3 style={modalTitle}>{flagModal.mode === 'flag' ? '🚩 Flag Project' : 'Remove Flag'}</h3>
            <p style={modalSub}>{flagModal.eng.client_name} — {flagModal.eng.teams?.name}</p>
            {flagModal.mode === 'flag' && (
              <textarea style={modalTextarea} placeholder="Reason for flagging…"
                value={noteInput} onChange={e => setNoteInput(e.target.value)} rows={3} />
            )}
            <div style={{display:'flex',gap:12,marginTop:16}}>
              <button style={primaryBtn} onClick={handleFlag}>
                {flagModal.mode === 'flag' ? 'Flag Project' : 'Remove Flag'}
              </button>
              <button style={cancelBtn} onClick={() => { setFlagModal(null); setNoteInput(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Suspend modal ── */}
      {suspendModal && (
        <div style={overlay}>
          <div style={modal}>
            <h3 style={modalTitle}>{suspendModal.mode === 'suspend' ? '⛔ Suspend Team' : 'Reinstate Team'}</h3>
            <p style={modalSub}>{suspendModal.team.name}</p>
            {suspendModal.mode === 'suspend' && (
              <textarea style={modalTextarea} placeholder="Reason for suspension…"
                value={noteInput} onChange={e => setNoteInput(e.target.value)} rows={3} />
            )}
            {suspendModal.mode === 'suspend' && (
              <div style={{...callout,marginBottom:0,marginTop:12}}>
                Warning: suspending a team will prevent all members from drawing new samples.
                Existing projects and data are preserved.
              </div>
            )}
            <div style={{display:'flex',gap:12,marginTop:16}}>
              <button style={{...primaryBtn,background:suspendModal.mode==='suspend'?'#c03a1a':AMBER,
                color:suspendModal.mode==='suspend'?'white':NAVY}} onClick={handleSuspend}>
                {suspendModal.mode === 'suspend' ? 'Suspend Team' : 'Reinstate Team'}
              </button>
              <button style={cancelBtn} onClick={() => { setSuspendModal(null); setNoteInput(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const page        = { minHeight:'100vh', background:'#f4f6fb', fontFamily:"'Montserrat',sans-serif" };
const nav         = { height:60, background:NAVY, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 28px', position:'sticky', top:0, zIndex:100 };
const backBtn     = { background:'none', border:'none', color:'rgba(255,255,255,0.6)', fontFamily:"'Roboto Mono',monospace", fontSize:12, cursor:'pointer', padding:0 };
const tabBar      = { background:'white', borderBottom:`1.5px solid ${BORDER}`, display:'flex', padding:'0 32px' };
const tabBtn      = { padding:'14px 20px', background:'none', border:'none', borderBottom:'3px solid transparent', fontFamily:"'Roboto Mono',monospace", fontSize:11, letterSpacing:'0.06em', textTransform:'uppercase', color:'#8a96b0', cursor:'pointer', position:'relative' };
const tabBtnActive= { borderBottomColor:NAVY, color:NAVY, fontWeight:700 };
const alertDot    = { position:'absolute', top:8, right:6, background:'#c03a1a', color:'white', borderRadius:10, fontSize:9, padding:'1px 5px', fontWeight:700 };
const body        = { maxWidth:1100, margin:'0 auto', padding:'32px 24px 80px' };
const headerRow   = { display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24, gap:24 };
const eyebrow     = { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:AMBER, marginBottom:4 };
const pageTitle   = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:24, color:NAVY, margin:0 };
const searchInput = { padding:'10px 14px', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, outline:'none', width:260 };
const alertBox    = { background:'#fff8e6', border:'1.5px solid #c98a00', borderRadius:6, padding:'12px 16px', fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#7a5c10', lineHeight:1.6 };
const table       = { width:'100%', borderCollapse:'collapse', fontFamily:"'Roboto Mono',monospace", fontSize:12 };
const th          = { background:NAVY, color:AMBER, padding:'8px 12px', textAlign:'left', fontSize:10, letterSpacing:'0.06em', textTransform:'uppercase' };
const td          = { padding:'10px 12px', color:'#5a6a8a', verticalAlign:'middle' };
const statusPill  = { borderRadius:20, padding:'2px 10px', fontSize:10, fontWeight:600, fontFamily:"'Roboto Mono',monospace" };
const flagBtn     = { padding:'4px 10px', background:'none', border:'1px solid #c8d0e0', borderRadius:4, fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#8a96b0', cursor:'pointer' };
const unflagBtn   = { padding:'4px 10px', background:'none', border:'1px solid #1a5c3a', borderRadius:4, fontFamily:"'Roboto Mono',monospace", fontSize:11, color:'#1a5c3a', cursor:'pointer' };
const sectionLabel= { fontFamily:"'Roboto Mono',monospace", fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'#8a96b0', margin:'0 0 12px', paddingTop:20, borderTop:'1px solid #e8ecf5' };
const overlay     = { position:'fixed', inset:0, background:'rgba(13,36,97,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 };
const modal       = { background:'white', borderRadius:8, padding:32, width:440, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' };
const modalTitle  = { fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:18, color:NAVY, margin:'0 0 6px' };
const modalSub    = { fontFamily:"'Roboto Mono',monospace", fontSize:12, color:'#8a96b0', marginBottom:16 };
const modalTextarea={ width:'100%', padding:'10px 12px', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Roboto Mono',monospace", fontSize:12, color:NAVY, outline:'none', resize:'vertical', boxSizing:'border-box' };
const callout     = { background:'#e8ecf5', borderLeft:`3px solid ${NAVY}`, borderRadius:'0 6px 6px 0', padding:'12px 16px', fontFamily:"'Roboto Mono',monospace", fontSize:11, color:NAVY, lineHeight:1.6 };
const primaryBtn  = { padding:'10px 20px', background:AMBER, color:NAVY, border:'none', borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, cursor:'pointer' };
const cancelBtn   = { padding:'10px 20px', background:'#f4f6fb', color:'#5a6a8a', border:`1.5px solid ${BORDER}`, borderRadius:6, fontFamily:"'Montserrat',sans-serif", fontWeight:600, fontSize:13, cursor:'pointer' };
